"use client";

import {
	BookOpen,
	FolderKanban,
	Paperclip,
	Plus,
	Search,
	SendHorizontal,
	XIcon,
} from "lucide-react";
import Image from "next/image";
import { zodResolver } from "@hookform/resolvers/zod";
import { memo, useCallback, useEffect, useId, useRef, useState } from "react";
import {
	type Control,
	type SubmitHandler,
	useController,
	UseControllerProps,
	useForm,
	useFormState,
	UseFormStateProps,
	useWatch,
} from "react-hook-form";

import { uploadChatFile } from "@/lib/api/s3";
import { cn, errorIntoString, isUserAdmin, isUserPremium } from "@/lib/utils";
import { formatServiceDisplayTitle } from "@/lib/utils/service-title";
import { getProxiedImageUrl } from "@/lib/utils/image-proxy";
import { useChatContext } from "@/app/admin/chat/context";
import { type TextMessageFormData, textMessageSchema } from "@/lib/schemas/inbox";
import {
	type ClientSupportPageContext,
	useSendChatTypingStatusMutation,
	useSendChatsMessagesByIdMutation,
} from "@/features/chat/chatApiInjection";
import { useSearchServicesQuery } from "@/features/services/serviceApiInjection";
import { CHAT_CONSTANTS } from "../../constants";

import { Form } from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import LoadingSpinner from "@/components/loadingComponents/loadingSpinner";
import type { CatalogSuggestion, Participant, Service } from "@/types";
import EmojiPicker from "./EmojiPicker";

const MAX_CATALOG_SUGGESTIONS = 5;
const CHAT_DRAFT_STORAGE_PREFIX = "techxeed:chat-draft:";
const CHAT_CATALOG_DRAFT_STORAGE_PREFIX = "techxeed:chat-catalog-draft:";

function cleanCatalogText(value?: string | null, max = 220) {
	return (value ?? "")
		.replace(/<[^>]*>/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.slice(0, max);
}

function serviceToCatalogSuggestion(service: Service): CatalogSuggestion {
	const firstImage = service.media?.find((item) => {
		if (!item || item.type?.toUpperCase() === "VIDEO") return false;
		return Boolean(item.displayUrl?.trim() || item.url?.trim());
	});
	const rawImageUrl =
		firstImage?.displayUrl?.trim() ||
		firstImage?.url?.trim() ||
		service.thumbnailImage?.trim() ||
		"";
	const imageUrl = getProxiedImageUrl(rawImageUrl) ?? rawImageUrl;
	const tags =
		service.subcategoryIds
			?.map((category) => category?.title)
			.filter((tag): tag is string => Boolean(tag?.trim()))
			.slice(0, 5) ?? [];

	return {
		type: "service",
		title: formatServiceDisplayTitle(service.title),
		label: service.categoryId?.title || service.eyebrowText || "Service",
		description: cleanCatalogText(service.excerpt || service.description),
		url: `/services/${service.slug || service._id}`,
		imageUrl,
		meta: tags.slice(0, 2).join(" · "),
		tags,
		service,
	};
}

type Props = {
	chatId: string;
	receiver: Participant;
	isReceiverDeleted?: boolean;
	isClientSupportGroup?: boolean;
	isLauraDirectorsGroup?: boolean;
	lauraPaused?: boolean;
	clientSupportSenderRole?: "CUSTOMER" | "STAFF";
	replyToMessageId?: string | null;
	replyTargetLabel?: string | null;
	onClearReplyTarget?: () => void;
	onExpectedAsyncReplyLock?: () => void;
	onExpectedAsyncReplyStart?: () => void;
	onExpectedAsyncReplyCancel?: () => void;
	onMessageSent?: () => void;
	compact?: boolean;
	glassmorphic?: boolean;
	disabledByLaura?: boolean;
	showLauraReplyHint?: boolean;
	pageContext?: ClientSupportPageContext;
	catalogRequest?: { id: number; suggestions: CatalogSuggestion[] } | null;
	allowAttachments?: boolean;
};

function shouldSkipExpectedLauraReply(message: string) {
	const normalized = message
		.trim()
		.toLowerCase()
		.replace(/[.!]+$/g, "")
		.replace(/\s+/g, " ");
	if (!normalized || normalized.includes("?") || normalized.length > 80) return false;

	return /^(thanks|thank you|thanks laura|thank you laura|thx|ty|got it|i got it|understood|noted|cool|great|perfect|awesome|nice|sounds good|alright|all right|fine|no worries|cheers|appreciate it|much appreciated)$/.test(
		normalized,
	);
}

function isLikelyAccidentalLauraMessage(message: string) {
	const normalized = message.trim().toLowerCase().replace(/\s+/g, " ");
	if (!normalized) return true;
	if (/^(attachment|service catalog)$/i.test(normalized)) return true;
	if (/^[^\p{L}\p{N}]+$/u.test(normalized)) return true;

	const compact = normalized.replace(/[^a-z0-9]/gi, "");
	if (!compact) return true;
	if (/^(.)\1{3,}$/i.test(compact)) return true;
	if (/^(.{2,3})\1{2,}$/i.test(compact)) return true;

	const letters = compact.replace(/[^a-z]/gi, "");
	if (letters.length >= 5) {
		const vowelCount = (letters.match(/[aeiou]/gi) ?? []).length;
		if (vowelCount === 0) return true;
		if (letters.length >= 8 && vowelCount / letters.length < 0.12) return true;
	}

	return false;
}

function shouldSkipIncompleteExpectedLauraReply(message: string) {
	const trimmed = message.trim();
	const normalized = trimmed.toLowerCase().replace(/\s+/g, " ");
	if (!normalized || /[?!.]$/.test(trimmed)) return false;

	if (
		/^(hi|hello|hey|help|help me|need help|i need help|yes|yeah|yep|no|nope|ok|okay)$/i.test(
			normalized,
		)
	) {
		return false;
	}

	if (
		/^(i|i want|i need|i would|can you|could you|would you|please|how do|how can|what is|what about|do you|the|this|that|my|your|it|is|are|can|could|would|should)$/i.test(
			normalized,
		)
	) {
		return true;
	}
	if (
		/\b(a|an|the|to|for|with|and|or|but|because|about|in|on|at|of|from|my|your|our|this|that|these|those|is|are|can|could|would|should|please)$/i.test(
			normalized,
		)
	) {
		return true;
	}

	const words = normalized.split(/\s+/).filter(Boolean);
	if (words.length === 1) {
		return words[0].replace(/[^a-z0-9']/gi, "").length <= 3;
	}

	return false;
}

function TextBar({
	chatId,
	receiver,
	isReceiverDeleted = false,
	isClientSupportGroup = false,
	isLauraDirectorsGroup = false,
	lauraPaused = false,
	clientSupportSenderRole,
	replyToMessageId = null,
	replyTargetLabel = null,
	onClearReplyTarget,
	onExpectedAsyncReplyLock,
	onExpectedAsyncReplyStart,
	onExpectedAsyncReplyCancel,
	onMessageSent,
	compact = false,
	glassmorphic = false,
	disabledByLaura = false,
	showLauraReplyHint = false,
	pageContext,
	catalogRequest,
	allowAttachments = true,
}: Props) {
	const { toast } = useToast();
	const { data } = useChatContext();
	const [isLoading, setStatus] = useState(false);
	const [showAiEntryComposer, setShowAiEntryComposer] = useState(false);
	const [aiEntryType, setAiEntryType] = useState<"KNOWLEDGE" | "RULE">("KNOWLEDGE");
	const [aiEntryTitle, setAiEntryTitle] = useState("");
	const [aiEntryContent, setAiEntryContent] = useState("");
	const [aiEntryTags, setAiEntryTags] = useState("");
	const [isComposerMenuOpen, setIsComposerMenuOpen] = useState(false);
	const [isCatalogModalOpen, setIsCatalogModalOpen] = useState(false);
	const [selectedCatalogSuggestions, setSelectedCatalogSuggestions] = useState<
		CatalogSuggestion[]
	>([]);
	const [sendMessage] = useSendChatsMessagesByIdMutation();
	const [sendTypingStatus] = useSendChatTypingStatusMutation();
	const senderId = data?._id;
	const canCreateAiEntry = isLauraDirectorsGroup && data?.role === "DIRECTOR";
	const fileInputId = useId();
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const lastTypingSentAtRef = useRef(0);
	const skipNextDraftSaveRef = useRef(false);

	const form = useForm<TextMessageFormData>({
		resolver: zodResolver(textMessageSchema),
		disabled:
			disabledByLaura ||
			(!isClientSupportGroup &&
				!isUserAdmin(data?.role) &&
				!isUserPremium(data?.plan?.duration) &&
				receiver._id !== process.env.NEXT_PUBLIC_CUSTOMER_SUPPORT_USER_ID),
		defaultValues: {
			content: "",
			files: [],
		},
	});
	const composerDisabled = form.formState.disabled || disabledByLaura;
	const draftStorageKey = `${CHAT_DRAFT_STORAGE_PREFIX}${chatId}`;
	const catalogDraftStorageKey = `${CHAT_CATALOG_DRAFT_STORAGE_PREFIX}${chatId}`;
	const draftContent = useWatch({ control: form.control, name: "content" });

	useEffect(() => {
		if (typeof window === "undefined") return;

		const savedDraft = window.sessionStorage.getItem(draftStorageKey) ?? "";
		const savedCatalogDraft = window.sessionStorage.getItem(catalogDraftStorageKey);
		skipNextDraftSaveRef.current = true;
		form.setValue("content", savedDraft, { shouldDirty: savedDraft.length > 0 });
		if (savedCatalogDraft) {
			try {
				const parsed = JSON.parse(savedCatalogDraft) as CatalogSuggestion[];
				if (Array.isArray(parsed)) {
					setSelectedCatalogSuggestions(parsed.slice(0, MAX_CATALOG_SUGGESTIONS));
				}
			} catch {
				// Ignore malformed handoff data and clear it below.
			}
			window.sessionStorage.removeItem(catalogDraftStorageKey);
		}
	}, [catalogDraftStorageKey, draftStorageKey, form]);

	useEffect(() => {
		const suggestions = catalogRequest?.suggestions;
		if (!catalogRequest?.id || !Array.isArray(suggestions) || suggestions.length === 0) return;

		setSelectedCatalogSuggestions((current) => {
			const merged = [...current];
			for (const suggestion of suggestions) {
				if (!suggestion?.url || merged.some((item) => item.url === suggestion.url)) continue;
				merged.push(suggestion);
			}
			return merged.slice(0, MAX_CATALOG_SUGGESTIONS);
		});
	}, [catalogRequest?.id, catalogRequest?.suggestions]);

	useEffect(() => {
		if (typeof window === "undefined") return;
		if (skipNextDraftSaveRef.current) {
			skipNextDraftSaveRef.current = false;
			return;
		}

		const nextDraft = typeof draftContent === "string" ? draftContent : "";
		if (nextDraft.length > 0) {
			window.sessionStorage.setItem(draftStorageKey, nextDraft);
		} else {
			window.sessionStorage.removeItem(draftStorageKey);
		}
	}, [draftContent, draftStorageKey]);

	const emitTypingStatus = useCallback(
		(isTyping: boolean) => {
			if (!chatId || composerDisabled) return;
			const now = Date.now();
			if (isTyping && now - lastTypingSentAtRef.current < 1500) return;
			lastTypingSentAtRef.current = now;
			void sendTypingStatus({ chatId, isTyping });
		},
		[chatId, composerDisabled, sendTypingStatus],
	);

	const handleTyping = useCallback(
		(value: string) => {
			if (typingTimeoutRef.current) {
				clearTimeout(typingTimeoutRef.current);
			}

			if (value.trim()) {
				emitTypingStatus(true);
				typingTimeoutRef.current = setTimeout(() => {
					emitTypingStatus(false);
				}, 2500);
			} else {
				emitTypingStatus(false);
			}
		},
		[emitTypingStatus],
	);

	useEffect(
		() => () => {
			if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
			emitTypingStatus(false);
		},
		[emitTypingStatus],
	);

	const onSubmit: SubmitHandler<TextMessageFormData> = async ({ content, files }) => {
		if (disabledByLaura) return;
		const trimmedContent = content.trim();
		const catalogSuggestions = selectedCatalogSuggestions.slice(0, MAX_CATALOG_SUGGESTIONS);
		const attachmentFiles = allowAttachments ? files : [];
		if (!trimmedContent && attachmentFiles.length === 0 && catalogSuggestions.length === 0) return;

		setStatus(true);
		if (typingTimeoutRef.current) {
			clearTimeout(typingTimeoutRef.current);
			typingTimeoutRef.current = null;
		}
		emitTypingStatus(false);

		const expectsLauraReply =
			Boolean(trimmedContent) &&
			catalogSuggestions.length === 0 &&
			isClientSupportGroup &&
			!lauraPaused &&
			clientSupportSenderRole === "CUSTOMER";
		const shouldLockForLaura =
			expectsLauraReply &&
			!shouldSkipExpectedLauraReply(trimmedContent) &&
			!isLikelyAccidentalLauraMessage(trimmedContent) &&
			!shouldSkipIncompleteExpectedLauraReply(trimmedContent);

		if (shouldLockForLaura) {
			onExpectedAsyncReplyLock?.();
		}

		let resp: Awaited<ReturnType<typeof sendMessage>>;
		try {
			const fileUrls = await Promise.all(
				attachmentFiles.map((x) =>
					uploadChatFile(x, {
						entityId: chatId,
						filePurpose: "attachment",
					}),
				),
			);
			resp = await sendMessage({
				chatId,
				content: trimmedContent || "Service catalog",
				fileUrls,
				catalogSuggestions,
				asCustomerSupport: false,
				senderId, // Pass senderId for optimistic update
				expectAsyncReply: expectsLauraReply,
				clientSupportSenderRole,
				replyToMessageId,
				pageContext: expectsLauraReply ? pageContext : undefined,
			});
		} catch (error) {
			if (shouldLockForLaura) {
				onExpectedAsyncReplyCancel?.();
			}
			setStatus(false);
			return toast({
				variant: "destructive",
				title: "Failed to send message",
				description: errorIntoString(error),
			});
		}

		setStatus(false);

		if ("error" in resp) {
			if (shouldLockForLaura) {
				onExpectedAsyncReplyCancel?.();
			}
			const ex = errorIntoString(resp.error);
			return toast({
				variant: "destructive",
				title: "Failed to send message",
				description: ex,
			});
		}

		if (shouldLockForLaura) {
			onExpectedAsyncReplyStart?.();
		}

		if (typeof window !== "undefined") {
			window.sessionStorage.removeItem(draftStorageKey);
		}
		form.reset();
		setSelectedCatalogSuggestions([]);
		onClearReplyTarget?.();
		onMessageSent?.();
	};

	const onFileRemove = useCallback(
		(index: number) => {
			const filtered = form.getValues("files");
			filtered.splice(index, 1);
			form.setValue("files", filtered);
		},
		[form],
	);

	const handleEmojiSelect = useCallback(
		(emoji: string) => {
			const currentContent = form.getValues("content") || "";
			form.setValue("content", currentContent + emoji);
		},
		[form],
	);

	const closeComposerMenu = useCallback(() => setIsComposerMenuOpen(false), []);
	const openFilePicker = useCallback(() => {
		closeComposerMenu();
		fileInputRef.current?.click();
	}, [closeComposerMenu]);
	const openCatalogModal = useCallback(() => {
		closeComposerMenu();
		setIsCatalogModalOpen(true);
	}, [closeComposerMenu]);
	const removeCatalogSuggestion = useCallback((url: string) => {
		setSelectedCatalogSuggestions((current) => current.filter((item) => item.url !== url));
	}, []);

	const submitAiEntry = async () => {
		const title = aiEntryTitle.trim();
		const entryContent = aiEntryContent.trim();
		if (!title || !entryContent || !senderId) {
			return toast({
				variant: "destructive",
				title: "Missing entry details",
				description: "Please add a title and content before sending it.",
			});
		}

		setStatus(true);
		const typeLabel = aiEntryType === "RULE" ? "Rule" : "Knowledge";
		const resp = await sendMessage({
			chatId,
			content: `Creating AI ${typeLabel}: ${title}`,
			fileUrls: [],
			asCustomerSupport: false,
			senderId,
			aiKnowledgeEntry: {
				title,
				content: entryContent,
				tags: aiEntryTags
					.split(",")
					.map((tag) => tag.trim())
					.filter(Boolean),
				entryType: aiEntryType,
				isActive: true,
			},
		});
		setStatus(false);

		if ("error" in resp) {
			return toast({
				variant: "destructive",
				title: `Failed to create AI ${aiEntryType === "RULE" ? "rule" : "knowledge"}`,
				description: errorIntoString(resp.error),
			});
		}

		toast({ title: `AI ${typeLabel} created` });
		setAiEntryTitle("");
		setAiEntryContent("");
		setAiEntryTags("");
		setAiEntryType("KNOWLEDGE");
		setShowAiEntryComposer(false);
	};

	if (isReceiverDeleted) {
		return (
			<div className="rounded-t-2xl border-t border-border bg-background px-4 py-4 text-center text-sm font-medium text-muted-foreground shadow-[0_-4px_12px_rgba(0,0,0,0.08)] dark:bg-card md:px-8">
				This user is not available to receive messages.
			</div>
		);
	}

	return (
		<Form {...form}>
			{replyToMessageId ? (
				<div className="mx-4 mb-2 flex items-center justify-between rounded-xl border border-secondary/20 bg-secondary/10 px-3 py-2 text-xs text-foreground md:mx-8">
					<span className="min-w-0">
						<span className="block font-semibold">Replying to</span>
						<span className="line-clamp-1 text-muted-foreground">
							{replyTargetLabel || "message"}
						</span>
					</span>
					<button
						type="button"
						className="ml-3 shrink-0 rounded-full p-1 text-muted-foreground hover:bg-background"
						onClick={onClearReplyTarget}
						aria-label="Clear selected reply"
					>
						<XIcon className="size-4" />
					</button>
				</div>
			) : null}
			<form
				onSubmit={form.handleSubmit(onSubmit)}
				className={cn(
					"grid min-h-16 max-w-[100vw] grid-cols-[1fr]",
					form.formState.disabled && "opacity-50",
				)}
			>
				{allowAttachments ? (
					<FilesSection control={form.control} onRemove={onFileRemove} />
				) : null}
				<CatalogSelectionSection
					suggestions={selectedCatalogSuggestions}
					onRemove={removeCatalogSuggestion}
				/>
				{showAiEntryComposer && canCreateAiEntry ? (
					<div className="col-span-full border-t border-border bg-background px-4 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.04)] dark:bg-card md:px-8">
						<div className="mx-auto max-w-3xl rounded-2xl border border-secondary/10 p-4">
							<div className="mb-3 flex items-start justify-between gap-3">
								<div>
									<p className="text-sm font-semibold text-foreground">
										Create AI Knowledge or Rule
									</p>
									<p className="text-xs text-muted-foreground">
										This will save directly to the AI Knowledge panel as an
										active entry.
									</p>
								</div>
								<button
									type="button"
									className="rounded-full p-1 text-muted-foreground hover:bg-muted"
									onClick={() => setShowAiEntryComposer(false)}
									aria-label="Close AI entry composer"
								>
									<XIcon className="size-4" />
								</button>
							</div>
							<div className="grid gap-3">
								<label className="text-xs font-medium text-muted-foreground">
									Type
									<select
										value={aiEntryType}
										onChange={(event) =>
											setAiEntryType(
												event.target.value as "KNOWLEDGE" | "RULE",
											)
										}
										className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
									>
										<option value="KNOWLEDGE">
											Knowledge - approved information Laura can use
										</option>
										<option value="RULE">
											Rule - behavior Laura must follow
										</option>
									</select>
								</label>
								<label className="text-xs font-medium text-muted-foreground">
									Title
									<input
										value={aiEntryTitle}
										onChange={(event) => setAiEntryTitle(event.target.value)}
										className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
										placeholder={
											aiEntryType === "RULE"
												? "Example: Laura escalation rule"
												: "Example: Hosting clarification"
										}
									/>
								</label>
								<label className="text-xs font-medium text-muted-foreground">
									Content
									<textarea
										value={aiEntryContent}
										onChange={(event) => setAiEntryContent(event.target.value)}
										className="mt-1 min-h-28 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
										placeholder={
											aiEntryType === "RULE"
												? "Write the rule Laura must follow..."
												: "Write the approved information Laura can use..."
										}
									/>
								</label>
								<label className="text-xs font-medium text-muted-foreground">
									Tags
									<input
										value={aiEntryTags}
										onChange={(event) => setAiEntryTags(event.target.value)}
										className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
										placeholder="laura, support, service"
									/>
								</label>
								<div className="flex justify-end">
									<button
										type="button"
										className="rounded-full bg-secondary px-4 py-2 text-xs font-semibold text-secondary-foreground disabled:opacity-60"
										onClick={submitAiEntry}
										disabled={
											isLoading ||
											!aiEntryTitle.trim() ||
											!aiEntryContent.trim()
										}
									>
										Send and create{" "}
										{aiEntryType === "RULE" ? "rule" : "knowledge"}
									</button>
								</div>
							</div>
						</div>
					</div>
				) : null}

				<div
					className={cn(
						"col-span-full grid min-h-16 items-center gap-2 rounded-t-2xl bg-background px-4 pb-3 pt-4 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] dark:bg-card md:px-8",
						compact && "gap-1 px-3 py-3 md:px-3",
						glassmorphic &&
							"border-t border-[color:var(--color-border-standard)] bg-background/55 shadow-[0_-4px_18px_rgba(7,168,188,0.12)] backdrop-blur-xl dark:bg-core-midnight/45",
						canCreateAiEntry
							? "grid-cols-[auto_auto_auto_1fr_auto]"
							: "grid-cols-[auto_auto_1fr_auto]",
					)}
				>
					{canCreateAiEntry ? (
						<button
							type="button"
							className={cn(
								"inline-flex size-10 items-center justify-center rounded-full transition-colors hover:bg-accent-foreground/10",
								compact && "size-8",
							)}
							onClick={() => setShowAiEntryComposer((current) => !current)}
							aria-label="Create AI Knowledge or Rule"
						>
							<BookOpen
								className={cn(
									"size-5 stroke-accent-foreground hover:stroke-accent-foreground/70",
									compact && "size-4",
								)}
							/>
						</button>
					) : null}
					<div className="relative">
						<button
							type="button"
							className={cn(
								"inline-flex size-10 items-center justify-center rounded-full transition-colors hover:bg-accent-foreground/10",
								compact && "size-8",
								composerDisabled && "pointer-events-none opacity-50",
							)}
							onClick={() => setIsComposerMenuOpen((current) => !current)}
							disabled={composerDisabled}
							aria-label="Open chat actions"
							aria-expanded={isComposerMenuOpen}
						>
							<Plus
								className={cn(
									"size-5 stroke-accent-foreground transition-transform hover:stroke-accent-foreground/70",
									compact && "size-4",
									isComposerMenuOpen && "rotate-45",
								)}
							/>
						</button>
						{isComposerMenuOpen ? (
							<div className="absolute bottom-full left-0 z-20 mb-2 flex min-w-44 flex-col gap-1 rounded-2xl border border-secondary/20 bg-background/95 p-2 text-sm shadow-xl backdrop-blur dark:bg-card/95">
								{allowAttachments ? (
									<button
										type="button"
										className="flex items-center gap-2 rounded-xl px-3 py-2 text-left text-foreground transition hover:bg-secondary/10"
										onClick={openFilePicker}
									>
										<Paperclip className="size-4 stroke-secondary" />
										<span>Attachment</span>
									</button>
								) : null}
								<button
									type="button"
									className="flex items-center gap-2 rounded-xl px-3 py-2 text-left text-foreground transition hover:bg-secondary/10"
									onClick={openCatalogModal}
								>
									<FolderKanban className="size-4 stroke-secondary" />
									<span>Service catalog</span>
								</button>
							</div>
						) : null}
						{allowAttachments ? (
							<input
								id={fileInputId}
								ref={fileInputRef}
								type="file"
								name="files"
								hidden
								multiple
								disabled={composerDisabled}
								onChange={(x) => form.setValue("files", Array.from(x.target.files!))}
								aria-label="Attach files to message"
							/>
						) : null}
					</div>
					<EmojiPicker
						onEmojiSelect={handleEmojiSelect}
						disabled={composerDisabled}
						className={compact ? "size-8" : undefined}
						iconClassName={compact ? "size-4" : undefined}
					/>
					<TextArea
						control={form.control}
						name="content"
						onTyping={handleTyping}
						compact={compact}
						glassmorphic={glassmorphic}
						disabledByLaura={disabledByLaura}
						showLauraReplyHint={showLauraReplyHint}
					/>
					<SubmitButton
						control={form.control}
						isLoading={isLoading}
						compact={compact}
						disabledByLaura={disabledByLaura}
						catalogSuggestionCount={selectedCatalogSuggestions.length}
					/>

					<ErrorBar control={form.control} />
				</div>
			</form>
			<ServiceCatalogPickerModal
				open={isCatalogModalOpen}
				selected={selectedCatalogSuggestions}
				onClose={() => setIsCatalogModalOpen(false)}
				onChange={setSelectedCatalogSuggestions}
			/>
		</Form>
	);
}

type FilesSectionProps = {
	control: Control<TextMessageFormData>;
	onRemove: (index: number) => void;
};

function CatalogSelectionSection({
	suggestions,
	onRemove,
}: {
	suggestions: CatalogSuggestion[];
	onRemove: (url: string) => void;
}) {
	if (suggestions.length === 0) return null;

	return (
		<div className="dark:bg-core-midnight/50 col-span-full flex flex-wrap gap-2 rounded-t-xl bg-accent-foreground/5 px-4 py-3 shadow-sm">
			{suggestions.map((suggestion) => (
				<div
					key={suggestion.url}
					className="group relative flex items-center gap-2 rounded-lg bg-background p-2 pr-8 text-sm shadow-sm"
				>
					<FolderKanban className="size-4 shrink-0 stroke-secondary" />
					<span className="max-w-48 truncate">{formatServiceDisplayTitle(suggestion.title)}</span>
					<button
						type="button"
						onClick={() => onRemove(suggestion.url)}
						className="absolute right-1 top-1 rounded-full p-1 opacity-70 transition-opacity hover:bg-destructive/10 hover:opacity-100"
						aria-label={`Remove ${formatServiceDisplayTitle(suggestion.title)}`}
					>
						<XIcon className="size-3" />
					</button>
				</div>
			))}
		</div>
	);
}

function ServiceCatalogPickerModal({
	open,
	selected,
	onClose,
	onChange,
}: {
	open: boolean;
	selected: CatalogSuggestion[];
	onClose: () => void;
	onChange: (suggestions: CatalogSuggestion[]) => void;
}) {
	const [search, setSearch] = useState("");
	const { data, isFetching } = useSearchServicesQuery(
		{
			search,
			page: 1,
			limit: 12,
			catalogParentsOnly: true,
		},
		{ skip: !open },
	);

	if (!open) return null;

	const selectedUrls = new Set(selected.map((item) => item.url));
	const services = data?.docs ?? [];

	const toggleService = (service: Service) => {
		const suggestion = serviceToCatalogSuggestion(service);
		if (selectedUrls.has(suggestion.url)) {
			onChange(selected.filter((item) => item.url !== suggestion.url));
			return;
		}
		if (selected.length >= MAX_CATALOG_SUGGESTIONS) return;
		onChange([...selected, suggestion]);
	};

	return (
		<div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center">
			<div className="w-full max-w-2xl rounded-2xl border border-secondary/20 bg-background shadow-2xl dark:bg-card">
				<div className="flex items-start justify-between gap-3 border-b border-secondary/20 px-4 py-3">
					<div>
						<p className="text-base font-semibold text-foreground">Service catalog</p>
						<p className="text-xs text-muted-foreground">
							Search and add up to {MAX_CATALOG_SUGGESTIONS} services to this message.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="rounded-full p-1 text-muted-foreground hover:bg-muted"
						aria-label="Close service catalog"
					>
						<XIcon className="size-5" />
					</button>
				</div>
				<div className="space-y-3 p-4">
					<label className="relative block">
						<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 stroke-secondary/60" />
						<input
							value={search}
							onChange={(event) => setSearch(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === "Enter") event.preventDefault();
							}}
							placeholder="Search services..."
							className="w-full rounded-xl border border-secondary/20 bg-background py-2 pl-9 pr-3 text-sm outline-none focus:border-secondary/60"
						/>
					</label>
					<div className="text-xs text-muted-foreground">
						{selected.length}/{MAX_CATALOG_SUGGESTIONS} selected
					</div>
					<div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
						{isFetching ? (
							<div className="flex justify-center py-8">
								<LoadingSpinner />
							</div>
						) : services.length === 0 ? (
							<p className="py-8 text-center text-sm text-muted-foreground">
								No services found.
							</p>
						) : (
							services.map((service) => {
								const suggestion = serviceToCatalogSuggestion(service);
								const isSelected = selectedUrls.has(suggestion.url);
								const isDisabled =
									!isSelected && selected.length >= MAX_CATALOG_SUGGESTIONS;
								return (
									<button
										key={service._id}
										type="button"
										onClick={() => toggleService(service)}
										disabled={isDisabled}
										className={cn(
											"flex w-full items-center gap-3 rounded-lg bg-card p-3 text-left shadow-custom transition-transform hover:-translate-y-0.5 disabled:opacity-50",
											isSelected && "bg-secondary/10 shadow-md",
										)}
									>
										<div className="relative size-12 shrink-0 overflow-hidden rounded-lg bg-muted">
											{suggestion.imageUrl ? (
												<img
													src={suggestion.imageUrl}
													alt=""
													className="size-full object-cover"
												/>
											) : null}
										</div>
										<div className="min-w-0 flex-1">
											<p className="line-clamp-1 text-sm font-semibold text-foreground">
												{formatServiceDisplayTitle(service.title)}
											</p>
											<p className="line-clamp-1 text-xs text-muted-foreground">
												{suggestion.description || "Techxeed service"}
											</p>
											{suggestion.tags?.length ? (
												<div className="mt-1 flex h-6 flex-nowrap items-center gap-1 overflow-hidden">
													{suggestion.tags.slice(0, 4).map((tag) => (
														<span
															key={tag}
															className="inline-flex max-w-[11rem] shrink-0 items-center truncate rounded-full bg-secondary/5 px-2 py-1 text-[9px] font-normal leading-tight text-secondary"
															title={tag}
														>
															{tag}
														</span>
													))}
												</div>
											) : null}
										</div>
										<span className="shrink-0 rounded-full border border-secondary/30 px-3 py-1 text-xs font-semibold text-secondary">
											{isSelected ? "Added" : "Add"}
										</span>
									</button>
								);
							})
						)}
					</div>
					<div className="flex justify-end gap-2 border-t border-secondary/20 pt-3">
						<button
							type="button"
							onClick={onClose}
							className="rounded-full bg-secondary px-4 py-2 text-sm font-semibold text-secondary-foreground"
						>
							Done
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

const FilesSection = memo(function FilesSection({ control, onRemove }: FilesSectionProps) {
	const files = useWatch({ control, name: "files" });
	const objectUrlsMapRef = useRef<Map<number, string>>(new Map());

	// Create and cleanup object URLs for image files
	useEffect(() => {
		const currentUrls = new Map<number, string>();

		files.forEach((file, index) => {
			if (
				file instanceof File &&
				file.type.startsWith("image/") &&
				file.size < CHAT_CONSTANTS.MAX_FILE_SIZE_BYTES
			) {
				const url = URL.createObjectURL(file);
				currentUrls.set(index, url);
			}
		});

		// Revoke old URLs that are no longer needed
		objectUrlsMapRef.current.forEach((url, index) => {
			if (!currentUrls.has(index)) {
				URL.revokeObjectURL(url);
			}
		});

		objectUrlsMapRef.current = currentUrls;

		return () => {
			// Revoke all URLs on cleanup
			objectUrlsMapRef.current.forEach((url) => URL.revokeObjectURL(url));
			objectUrlsMapRef.current.clear();
		};
	}, [files]);

	if (files.length === 0) return null;

	const getFileIcon = (file: File) => {
		const type = file.type.split("/")[0];
		if (type === "image") return "🖼️";
		if (type === "video") return "🎥";
		if (file.type.includes("pdf")) return "📄";
		if (file.type.includes("word") || file.type.includes("document")) return "📝";
		if (file.type.includes("sheet") || file.type.includes("excel")) return "📊";
		return "📎";
	};

	const formatFileSize = (bytes: number) => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	return (
		<div className="dark:bg-core-midnight/50 col-span-full flex flex-wrap gap-2 rounded-t-xl bg-accent-foreground/5 px-4 py-3 shadow-sm">
			{files.map((x, i) => {
				const isImage = x.type.startsWith("image/");
				const imageUrl =
					isImage && x.size < CHAT_CONSTANTS.MAX_FILE_SIZE_BYTES
						? objectUrlsMapRef.current.get(i)
						: null;

				return (
					<div
						key={`${x.name}-${i}`}
						className="group relative flex items-center gap-2 rounded-lg bg-background p-2 pr-8 shadow-sm"
					>
						{imageUrl ? (
							<Image
								src={imageUrl}
								alt={x.name}
								width={48}
								height={48}
								className="size-12 rounded object-cover"
								unoptimized
							/>
						) : (
							<span className="flex size-12 items-center justify-center text-2xl">
								{getFileIcon(x)}
							</span>
						)}
						<div className="flex min-w-0 flex-1 flex-col">
							<span className="truncate text-xs font-medium text-foreground">
								{x.name}
							</span>
							<span className="text-[0.65rem] text-muted-foreground">
								{formatFileSize(x.size)}
							</span>
						</div>
						<button
							type="button"
							className="absolute right-1 top-1 size-5 cursor-pointer rounded-full text-grey-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
							onClick={() => onRemove.call(undefined, i)}
							aria-label={`Remove ${x.name}`}
						>
							<XIcon className="size-3 md:size-4" />
						</button>
					</div>
				);
			})}
		</div>
	);
});

type SubmitButtonProps = {
	control: Control<TextMessageFormData>;
	isLoading: boolean;
	compact?: boolean;
	disabledByLaura?: boolean;
	catalogSuggestionCount?: number;
};

function SubmitButton({
	control,
	isLoading,
	compact = false,
	disabledByLaura = false,
	catalogSuggestionCount = 0,
}: SubmitButtonProps) {
	const { content, files } = useWatch({ control });

	const enabled =
		!disabledByLaura &&
		((content !== undefined && content.trim().length > 0) ||
			(files !== undefined && files.length > 0) ||
			catalogSuggestionCount > 0);

	return (
		<button
			type="submit"
			disabled={!enabled || isLoading}
			className={cn(
				"inline-flex size-10 cursor-pointer items-center justify-center rounded-full bg-secondary text-white transition-all disabled:cursor-not-allowed disabled:bg-secondary/70 hover:bg-secondary/90 hover:shadow-md active:scale-95",
				compact && "size-10",
			)}
			aria-label="Send message"
		>
			{isLoading ? (
				<LoadingSpinner fullScreen={false} layout="inline" className="!h-6 !w-6" />
			) : (
				<SendHorizontal
					className="size-4 !text-white"
					style={{ color: "white", stroke: "white" }}
					strokeWidth={2.5}
				/>
			)}
		</button>
	);
}

function TextArea(
	props: UseControllerProps<TextMessageFormData> & {
		onTyping?: (value: string) => void;
		compact?: boolean;
		glassmorphic?: boolean;
		disabledByLaura?: boolean;
		showLauraReplyHint?: boolean;
	},
) {
	const {
		onTyping,
		compact = false,
		glassmorphic = false,
		disabledByLaura = false,
		showLauraReplyHint = false,
		...controllerProps
	} = props;
	const { field, formState } = useController(controllerProps);
	const { value, onChange, onBlur, name, ref } = field;
	const disabled = formState.disabled || disabledByLaura;

	return (
		<textarea
			name={name}
			ref={ref}
			onChange={(event) => {
				onChange(event);
				onTyping?.(event.target.value);
			}}
			onBlur={onBlur}
			disabled={disabled}
			value={typeof value === "string" ? value : ""}
			maxLength={5000}
			placeholder={
				showLauraReplyHint || disabledByLaura
					? "Laura is replying..."
					: formState.disabled
						? "Only premium users can send messages to this chat"
						: "Type a message"
			}
			className={cn(
				"max-h-32 min-h-10 resize-none overflow-y-auto rounded-md bg-white px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none dark:bg-core-midnight md:text-base",
				compact && "px-2 md:text-sm",
				glassmorphic &&
					"border border-[color:var(--color-border-standard)] bg-white/75 backdrop-blur-sm dark:bg-[rgba(11,21,27,0.72)] dark:text-foreground dark:placeholder:text-muted-foreground",
			)}
			style={
				{
					// @ts-ignore - experimental CSS property fieldSizing
					fieldSizing: "content",
				} as React.CSSProperties
			}
		/>
	);
}

function ErrorBar(props: UseFormStateProps<TextMessageFormData>) {
	const { toast } = useToast();
	const { errors } = useFormState(props);

	useEffect(() => {
		const err = Object.values(errors).find((x) => Array.isArray(x) || !!x.message);
		if (err === undefined) return;

		const readable = Array.isArray(err) ? err[0].message : err?.message;

		toast({ variant: "destructive", title: "Invalid", description: readable });
	}, [errors, toast]);

	return null;
}

export default memo(TextBar);
